import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { withI18next } from 'lib/i18n/withI18next'
import Typo from 'constants/typography'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { hasLocizeTranslation } from 'lib/utils/locize'
import clsx from 'clsx'
import ReviewApiHandler from 'lib/api/review'
import Router from 'next/router'
import ProductReviewsEmpty from 'components/pdp/shared-components/product-reviews-empty/ProductReviewsEmpty'
import StateStorage from 'lib/state-storage'
import useIntersection from 'components/custom-hooks/useIntersection'
import styles from './product-reviews.scss'
import ProductReviewsAverageRatings from './product-reviews-average-ratings/ProductReviewsAverageRatings'
import ProductReviewsList from '../../shared-components/product-reviews-list/ProductReviewsList'
import { segmentPdpSeeAllReviewsClicked } from '../pdp-reviews-segment/pdpReviewsSegment'

const ProductReviews = ({
  t,
  productId,
  productAverageRatings,
  category_link_rewrite,
  product_link_rewrite,
  internationalization: { country, language },
}) => {
  const [productReviewsList, setProductReviewsList] = useState([])
  const isEmptyReviews = productReviewsList.length === 0
  const token = StateStorage.getAuthToken()
  const containerRef = useRef()
  const isVisible = useIntersection(containerRef, { rootMargin: '200px' })

  // events
  const handleRoute = () => {
    Router.push(
      `/review/product?id_product=${productId}&category_link_rewrite=${category_link_rewrite}&product_link_rewrite=${product_link_rewrite}`,
      `/${country}/${language}/review/product/${productId}/${category_link_rewrite}/${product_link_rewrite}`,
    )
    segmentPdpSeeAllReviewsClicked({
      id_product: productId,
    })
  }

  // effects
  useEffect(() => {
    const fetchProductReviewsList = async () => {
      try {
        const result = await ReviewApiHandler.getProductReviews({
          id_product: productId,
        })
        if (result) {
          const customerReviews = result?.customer_product_reviews || []
          const otherReviews = result?.product_reviews || []
          setProductReviewsList([...customerReviews, ...otherReviews])
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error(error)
      }
    }
    if (token && isVisible) {
      fetchProductReviewsList()
    }
  }, [productId, token, isVisible])

  return (
    <div
      ref={containerRef}
      data-cy="product-reviews-container"
      className="product-reviews-container"
    >
      <style jsx>{styles}</style>
      <div className="product-reviews-container__title-wrapper">
        <div className={clsx(Typo.title3, 'product-reviews-container__title')}>
          {hasLocizeTranslation(t, 'REVIEWS', 'Reviews')}
        </div>
        {!isEmptyReviews && (
          <div
            data-cy="product-reviews-container__see-all"
            className={clsx('product-reviews-container__see-all', Typo.body1)}
            onClick={handleRoute}
            onKeyDown={handleRoute}
            role="button"
            tabIndex={0}
          >
            {hasLocizeTranslation(t, 'SEE_ALL', 'SEE ALL')}
          </div>
        )}
      </div>
      {isEmptyReviews ? (
        <ProductReviewsEmpty productId={productId} />
      ) : (
        <React.Fragment>
          {productAverageRatings?.avg_overall_product_rating && (
            <ProductReviewsAverageRatings
              productId={productId}
              productAverageRatings={productAverageRatings}
            />
          )}
          <ProductReviewsList productReviewsList={productReviewsList} />
        </React.Fragment>
      )}
    </div>
  )
}

ProductReviews.defaultProps = {
  productAverageRatings: {},
}

ProductReviews.propTypes = {
  // component props
  productId: PropTypes.number.isRequired,
  category_link_rewrite: PropTypes.string.isRequired,
  product_link_rewrite: PropTypes.string.isRequired,
  productAverageRatings: PropTypes.shape({
    avg_design_and_style_rating: PropTypes.number,
    avg_overall_product_rating: PropTypes.number,
    avg_product_as_display_rating: PropTypes.number,
    avg_product_quality_rating: PropTypes.number,
    avg_rating_true_to_size: PropTypes.string,
    total_count: PropTypes.number,
  }),

  // redux props
  t: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
    language: PropTypes.string,
  }).isRequired,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
    internationalization: state.internationalization,
  })),
  withI18next(),
)(ProductReviews)
